import {
  MainCore,
  game,
  timeManager,
  CameraTypes,
  settings,
  SettingsTypes,
  gameStats,
  CustomEvents,
  THREE,
  AudioManager,
  CameraStates,
  cameraManager,
  requestManager,
  TimesTypes,
  fpsManager,
  appWSM2021Config,
  modes,
  AppWSM2021DifficultyTypes,
  playersConfig,
  PlayersSortTypes,
  playersManager,
  corePhasesManager,
  PlayersResultTypes,
  CorePhases,
  RoundingType,
  lightConfig
} from '@powerplay/core-minigames'
import {
  audioConfig,
  batchingConfig,
  modelsConfig,
  texturesConfig,
  debugConfig,
  cameraConfig,
  gameConfig,
  translatesReplacements,
  sponsorsConfig
} from './config'
import {
  TexturesNames,
  MaterialsNames,
  DisciplinePhases,
  PlayerStates
} from './types'
import { worldEnv } from './entities/env/WorldEnv'
import { player } from './entities/athlete/player'
import { inputsManager } from './InputsManager'
import { disciplinePhasesManager } from './phases/DisciplinePhasesManager'
import { appWSM2021AllDifficultiesConfig } from './config/appWSM2021AllDifficultiesConfig'
import { materialsConfig } from './config/materialsConfig'
import { trainingTasks } from './modes/training/TrainingTasks'
import { stateManager } from './StateManager'
import { tutorialFlow } from './modes/tutorial/TutorialFlow'
import { pathAssetsJavelin } from '@/globals/globalvariables'
import { linesManager } from './entities/env/LinesManager'
import { wind } from './entities/athlete/Wind'
import * as Sentry from '@sentry/vue'
import { audioHelper } from './audioHelper/AudioHelper'
import { endManager } from './EndManager'
import {
  tutorialState,
  debugState,
  trainingState
} from '@/stores'
import { loadingState } from '@powerplay/core-minigames-ui-ssm'
import { timeLimitManager } from './TimeLimitManager'

/**
 * Hlavna trieda pre disciplinu
 */
export class Main {

  /** Hlavna trieda z CORE */
  private mainCore: MainCore

  // Aktualna pozicia kamery
  private actualCameraPosition = new THREE.Vector3()

  // Aktualna rotacia kamery
  private actualCameraQuaternion = new THREE.Quaternion()

  /** Pause prveho tutorialu */
  private pausedFirstTutorial = false

  /** Clock timer */
  private clock = new THREE.Clock()

  // kam sa ma pozerat kamera pri emocnej kamere
  private lookAtEmotionVector = new THREE.Vector3()

  /**
   * Konstruktor
   */
  public constructor() {

    this.addListeners()
    // pripravenie konfigov pre WSM 2021 - musime kontrolvat takto kvoli typescriptu
    if (modes.isAppWSM2021()) {

      appWSM2021Config.data = appWSM2021AllDifficultiesConfig[
        modes.mode as unknown as AppWSM2021DifficultyTypes // small TS hack :)
      ]

    }

    /*
     * Nastavenie players konfigov, aby sa dobre zoradovali a mali dobre empty vysledky
     * nemusime zatial nic nastavovat, lebo berieme default hodnoty z konfigu
     */
    this.setPlayersConfig()
    /**
     * Nastavenie specialnych sponzorov
     */
    this.setSpecialSponsor()

    // musime este nastavit, aby nebolo intro v treningu
    corePhasesManager.showIntro = !modes.isTrainingMode()

    lightConfig.lightVector.set(-35, 70, -10)

    // spustime CORE veci a pokial vsetko je v pohode, pusti sa INIT metoda
    this.mainCore = new MainCore(
      {
        meshesCastShadows: materialsConfig[MaterialsNames.athlete].meshesArray || [],
        meshesAnisotropy: [],
        materials: materialsConfig,
        callbacks: {
          inputs: {
            callbackLeft: inputsManager.onKeyLeft,
            callbackRight: inputsManager.onKeyRight,
            callbackUp: inputsManager.onKeyUp,
            callbackDown: inputsManager.onKeyDown,
            callbackAction: inputsManager.onKeyAction,
            callbackAction2: inputsManager.onKeyAction2,
            callbackAction3: inputsManager.onKeyAction3,
            callbackExit: inputsManager.onKeyExit,
            callbackPrepareVideo: inputsManager.onKeyPrepareVideo
          },
          setSpecialDataFromInitRequest: this.setSpecialDataFromInitRequest,
          createAssets: this.createAssets,
          beforeGameStart: this.beforeGameStart,
          updateBeforePhysics: this.updateBeforePhysics,
          updateAfterPhysics: this.updateAfterPhysics,
          updateAnimations: this.updateAnimations
        },
        batching: batchingConfig,
        debugConfig,
        numberOfAttempts: modes.isTrainingMode() ?
          gameConfig.numberOfAttemptsTraining :
          gameConfig.numberOfAttempts,
        inputSchema: 'biathlon'
      },
      translatesReplacements,
      {
        textures: texturesConfig,
        models: modelsConfig,
        audio: audioConfig
      }
    )

    this.initialSetup()

  }

  /**
   * Nastavenie specialneho sponzora
   */
  private setSpecialSponsor(): void {

    const timeNow = Date.now()
    const random = THREE.MathUtils.randFloat(0, 1)
    const sponsorBannerToShow = {
      name: '',
      percent: 0,
      textureName: ''
    }

    // zatial kontrolujeme iba bannery
    sponsorsConfig.banners.forEach((sponsor) => {

      if (!sponsor.active) return

      // sponsor.name
      sponsor.times.forEach((slot) => {

        if (slot.from <= timeNow && slot.to > timeNow && random < slot.percent) {

          sponsorBannerToShow.name = sponsor.name
          sponsorBannerToShow.percent = slot.percent
          sponsorBannerToShow.textureName = sponsor.textureName
          console.log('vyberame sponzora pre bannery: ', sponsorBannerToShow)

        }

      })

    })

    // ak je nejaky sponzor - vyberie sa posledny
    if (sponsorBannerToShow.name !== '') {

      // zmenime assety - vymazeme povodny a dame novy
      delete texturesConfig[TexturesNames.colorGradient]
      materialsConfig[MaterialsNames.colorGradient].textureName = sponsorBannerToShow.textureName

    } else {

      // tu dame vsetky specialne
      delete texturesConfig[TexturesNames.colorGradientVshosting]

    }

  }

  /**
   * Metoda na overenie a zobrazenie FPS
   */
  private checkFpsRequest(): void {

    if (stateManager.getFpsStarted()) {

      const settingsQuality = settings.getSetting(SettingsTypes.quality)
      const fpsData = {
        averageFps: fpsManager.getAverageFpsByQuality(settingsQuality),
        actualFps: fpsManager.getActualFpsByQuality(settingsQuality),
        actualAverageFps: fpsManager.getActualAverageFps()
      }
      stateManager.setFpsData(fpsData)

    }

  }

  /**
   * Pridanie listenerov
   */
  private addListeners() {

    window.addEventListener(CustomEvents.readyForDisciplineInit, this.init)
    window.addEventListener(CustomEvents.loadingProgress, this.loadingProgress)
    // nastavime pocuvanie na zaciatok disciplinovej fazy z CORE
    window.addEventListener(
      CustomEvents.startDisciplinePhase,
      disciplinePhasesManager.setStartPhase
    )

  }

  /**
   * Inicializacny setup
   */
  private initialSetup() {

    const localEnv = Number(import.meta.env.VITE_APP_LOCAL) === 1
    this.mainCore.setIsLocalEnv(localEnv)
    game.setIsLocalEnv(localEnv)
    game.setCallbackCustomUpdateQuality(this.onUpdateQuality)

    // lokalne si davame ID discipliny, aby sme nemuseli menit v GET parametroch stale
    if (localEnv) requestManager.disciplineID = 16

    AudioManager.PATH_ASSETS = pathAssetsJavelin
    disciplinePhasesManager.create()

    corePhasesManager.provisionalResultsAllowed = !modes.isTutorial()
    corePhasesManager.provisionalResultsFrequency = modes.isTutorial() ? 9999 : 3
    /*
     * listener na zistenie appky, ze sme v background mode a mame dat pauzu, aby sme setrili
     * prostriedky a aby nehrali zvuky
     */
    window.addEventListener(CustomEvents.toggleBackgroundMode, () => {

      tutorialState().settings = true

    }, false)

  }

  /**
   * Vratenie ignorovanych nazvov textur
   * @returns Pole nazvov ignorovanych textur
   */
  private getIgnoredTexturesNames(): string[] {

    const allRaceTextures = [
      TexturesNames.athleteRaceBlackMan,
      TexturesNames.athleteRaceBlackWoman,
      TexturesNames.athleteRaceMulattoMan,
      TexturesNames.athleteRaceMulattoWoman,
      TexturesNames.athleteRaceWhiteMan,
      TexturesNames.athleteRaceWhiteWoman
    ]

    const usedTextures: string[] = []

    // pridame hraca
    const playerInfo = playersManager.getPlayer()
    usedTextures.push(`${playerInfo.sex}/${TexturesNames.athleteRacePrefix}${playerInfo.race}`)

    // vysledok bude rozdiel dvoch poli
    return allRaceTextures.filter(x => !usedTextures.includes(x))

  }

  /**
   * Inicializacia main core
   */
  public init = (): void => {

    // musime nastavit prefix pre meshe, aby nam nekolidovali meshe zo skyboxu s inymi
    game.prefixMeshesGroupEnabled = true

    game.shadowsManager.setShadowPlaneResolution(50, 50)

    this.mainCore.init(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getIgnoredTexturesNames(),
      TexturesNames.athleteRacePrefix
    )

    const tweenSettingsForCameraStates = cameraConfig.tweenSettingsForCameraStates

    this.mainCore.setTweenSettingsForStates(tweenSettingsForCameraStates)

    cameraManager.changeBaseRenderSettings(0.1, 2500)
    trainingTasks.initTraining()
    timeManager.setActive(TimesTypes.total, true)

    // UI update
    stateManager.allowDirectionState()

  }

  /**
   * Nastavenie konfigu pre hracov
   */
  private setPlayersConfig(): void {

    playersConfig.addTimes = false
    playersConfig.resultType = PlayersResultTypes.meters
    playersConfig.sortType = PlayersSortTypes.descending
    playersConfig.roundingStandings = RoundingType.floor
    playersManager.setCompareSkillBeforeTimestamp()

  }

  /**
   * Zobrazenie progresu loadingu
   */
  private loadingProgress = (): void => {

    gameStats.setNextLoadingPart()

    loadingState().loadingProgress = gameStats.getLoadingProgress()

  }

  /**
   * Nastavenie specialnych dat z init requestu
   * @param data - Specialne data
   */
  private setSpecialDataFromInitRequest = (data: unknown): void => {

    console.log(data)

    Sentry.setContext('minigame', {
      id: requestManager.MINIGAME_START_ID,
      idUser: player.uuid
    })

  }

  /**
   * Nastavenie assetov
   */
  private createAssets = (): void => {

    linesManager.init()

    wind.setValue()
    player.create()

    worldEnv.create()

    this.setUpDebug()

    // tiene
    game.shadowsManager.attachPlaneToObject(player.athleteObject)

    /*
     * TEMP
     * game.scene.fog = new THREE.Fog( 0xcccccc, 20, 400 )
     */

  }

  /**
   * Ak sa zmeni kvalita, vykonaju sa nasledujuce veci, pre optimalizaciu performance
   * @param settingValue - Hodnota kvality
   */
  public onUpdateQuality = (settingValue: number): void => {

    // hybanie skyboxu
    worldEnv.skybox.movingEnabled = settingValue >= 5

  }

  /**
   * puts singletons into window object
   */
  private setUpDebug(): void {

    if (!Number(import.meta.env.VITE_APP_LOCAL)) return

    const debug = {
      worldEnv,
      inputsManager,
      player,
      disciplinePhasesManager,
      cameraManager: cameraManager,
      setHUDVisibility: () => (
        debugState().isHudActive = true
      ),
      pauseGame: () => {

        if (game.paused) game.resumeGame()
        else game.pauseGame()

      },
      scene: game.scene,
      game,
      THREE,
      linesManager,
      changeSun: (newValue: number) => {

        worldEnv.skybox.updateSunPosition(newValue)

      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).debug = debug

  }

  /**
   * Nastavenie alebo spustenie veci pred startom hry
   */
  private beforeGameStart = (): void => {

    stateManager.setBeforeGameStartPhase()
    endManager.setWorldRecord()

  }

  /**
   * Spustenie veci v update pred vykonanim fyziky
   */
  private updateBeforePhysics = (): void => {

    if (
      !corePhasesManager.isActivePhaseByType(CorePhases.intro) &&
      !corePhasesManager.isActivePhaseByType(CorePhases.discipline)
    ) return

    if (timeLimitManager.check()) return

    disciplinePhasesManager.update()
    player.updateBeforePhysics(disciplinePhasesManager.actualPhase)
    this.checkFpsRequest()

  }

  /**
   * Spustenie veci v update po vykonani fyziky
   */
  private updateAfterPhysics = (delta: number): void => {

    if (
      !corePhasesManager.isActivePhaseByType(CorePhases.intro) &&
      !corePhasesManager.isActivePhaseByType(CorePhases.discipline)
    ) return

    if (requestManager.isFirstTrainingTutorial() && !this.pausedFirstTutorial) {

      this.pausedFirstTutorial = true
      console.log(requestManager.TUTORIAL_ID)
      trainingState().firstTutorialMessage = true
      game.pauseGame()
      return

    }
    player.updateAfterPhysics()

    this.actualCameraPosition.copy(player.getPosition())
    if (
      disciplinePhasesManager.actualPhase > DisciplinePhases.start &&
      disciplinePhasesManager.actualPhase <= DisciplinePhases.finish
    ) {

      this.actualCameraQuaternion.slerp(
        player.getQuaternion(),
        gameConfig.cameraQuaternionLerp
      )

    } else {

      this.actualCameraQuaternion.copy(player.getQuaternion())

    }

    let focusOnJavelin = false
    let posX = player.athleteObject.position.x
    let posZ = player.athleteObject.position.z
    if (player.javelin.wasThrown && player.javelin.javelinObjectFlying) {

      this.actualCameraPosition.copy(player.javelin.javelinObjectFlying?.position)
      focusOnJavelin = true
      posX = player.javelin.javelinObjectFlying?.position.x
      posZ = player.javelin.javelinObjectFlying?.position.z

    }

    // tiene
    worldEnv.manageShadowsPosition(posX, posZ)

    const instalerp = [DisciplinePhases.start, DisciplinePhases.preStart].includes(disciplinePhasesManager.actualPhase)
    const quatSettings = {
      lerpCoef: instalerp ? 1 : 0.1,
      newQuat: player.getQuaternion().clone()
    }

    cameraManager.move(
      this.actualCameraPosition,
      this.actualCameraQuaternion,
      delta,
      [/* hill.hillMesh */],
      cameraConfig.distanceFromGround,
      cameraManager.isThisCameraState(CameraStates.disciplineOutro) ||
        player.speedLockActive,
      (focusOnJavelin || player.isState(PlayerStates.emotion)) ? undefined : quatSettings
    )

    if (focusOnJavelin && player.javelin.javelinObjectFlying) {

      cameraManager.getMainCamera().lookAt(player.javelin.javelinObjectFlying.position)

    }

    if (player.isState(PlayerStates.emotion)) {

      this.lookAtEmotionVector.set(
        player.athleteObject.position.x,
        cameraConfig.emotion.lookAtPositionY,
        player.athleteObject.position.z
      )

      cameraManager.getMainCamera().lookAt(this.lookAtEmotionVector)

    }

    stateManager.setUpdateTimeState()

    if (modes.isTutorial()) tutorialFlow.update()

    worldEnv.update()

    audioHelper.update()

  }

  /**
   * Spustenie vykonavania vsetkych animacii
   * @param delta - Delta
   */
  private updateAnimations = (delta: number): void => {

    worldEnv.updateWithAnimations(this.clock.getElapsedTime())

    if (
      !corePhasesManager.isActivePhaseByType(CorePhases.intro) &&
      !corePhasesManager.isActivePhaseByType(CorePhases.discipline)
    ) return

    player.updateAnimations(delta)

  }

  /**
   * Zmena kamery na debug, ak by sme potrebvalo
   */
  public changeCameraToDebug = (): void => {

    cameraManager.setCamera(CameraTypes.debug)

  }

}
